// app/auth/login/Login.js
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { makeStyles } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import { yupResolver } from '@hookform/resolvers/yup';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { submitLogin, submitGroupChoice, submitRoleChoice, resetLoginState } from 'app/auth/store/loginSlice';
import { logoutUserSilent } from 'app/auth/store/userSlice';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import _ from '@lodash';
import { useTranslation } from 'react-i18next';
import ChooseGroupPanel from './ChooseGroupPanel';
import ChooseRolePanel from './ChooseRolePanel';
import nBossConnection from 'modules/base/service/nBossConnection';

const NO_GROUP = {
    title: 'Nincs csoport hozzárendelve a fiókodhoz',
    content: 'A fiókodhoz nincs csoport hozzárendelve. Kérlek vedd fel a kapcsolatot az Advanced Modelling Team-mel.',
    emailrecipient: 'advancedmodellingteam@mol.hu',
    emailsubject: 'Hozzáférés igénylése',
    emailcontent: 'Kedves Advanced Modelling Team,\n\nHozzáférést szeretnék igényelni.\n\nNév: {name}\nFelhasználás célja: {purpose}\nEmail: {email}\nTelefon: {phone}\n\nÜdvözlettel,\n{name}'
};

const NO_ROLE = {
    title: 'Nincs szerepkör hozzárendelve a fiókodhoz',
    content: 'A fiókodhoz nincs szerepkör hozzárendelve. Kérlek vedd fel a kapcsolatot az Advanced Modelling Team-mel.',
    emailrecipient: 'advancedmodellingteam@mol.hu',
    emailsubject: 'Hozzáférés igénylése',
    emailcontent: 'Kedves Advanced Modelling Team,\n\nHozzáférést szeretnék igényelni.\n\nNév: {name}\nFelhasználás célja: {purpose}\nEmail: {email}\nTelefon: {phone}\n\nÜdvözlettel,\n{name}'
};

const SAML_LOGIN = false;

const useStyles = makeStyles((theme) => ({ root: { background: `linear-gradient(to right, ${theme.palette.primary.dark} 0%, ${darken(theme.palette.primary.dark, 0.5)} 100%)`, color: theme.palette.primary.contrastText }, leftSection: {} }));
const defaultValues = { email: '', password: '' };
const _mailto = ({ to, subject, body }) => `mailto:${encodeURIComponent(String(to || ''))}?subject=${encodeURIComponent(String(subject || ''))}&body=${encodeURIComponent(String(body || ''))}`;

const _backendMsg = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    const direct = item.directMessage === true;
    const msg = direct ? item.message : (item.message || item.key);
    return msg ? String(msg) : '';
};

const _applyPropertiesErrors = (properties, setError, knownFields, fallbackField = 'email') => {
    if (!properties || typeof properties !== 'object') return false;

    let did = false;

    Object.entries(properties).forEach(([field, raw]) => {
        const list = Array.isArray(raw) ? raw : [raw];
        const msg = list.map(_backendMsg).filter(Boolean).join(' • ');
        if (!msg) return;

        const safeField = (typeof field === 'string' && knownFields.includes(field)) ? field : fallbackField;
        setError(safeField, { type: 'server', message: msg });
        did = true;
    });

    return did;
};

function Login() {
    const classes = useStyles();
    const { t } = useTranslation(['auth', 'validation']);
    const dispatch = useDispatch();

    const SAML_REDIRECT_URL = `${String(nBossConnection.baseUrl || '').replace(/\/+$/, '')}/samlUserLogin/redirect/`;

    const login = useSelector(({ auth }) => auth.login);
    const inProgress = !!login?.inProgress;

    const isChoosing = login?.nextStep === 'chooseGroup' || login?.nextStep === 'chooseRole';
    const [choiceFlow, setChoiceFlow] = useState(false);

    const [emailLoginMode, setEmailLoginMode] = useState(!SAML_LOGIN);

    const schema = yup.object().shape({ email: yup.string().email(t('validation:EMAIL_IS_NOT_VALID')).required(t('validation:EMAIL_IS_REQUIRED')), password: yup.string().required(t('validation:PASSWORD_REQUIRED')).min(5, t('validation:PASSWORD_IS_TOO_SHORT')) });
    const { control, setValue, formState, handleSubmit, reset, trigger, setError } = useForm({ mode: 'onChange', defaultValues, resolver: yupResolver(schema) });
    const { isValid, dirtyFields, errors } = formState;
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => { setValue('email', '', { shouldDirty: true, shouldValidate: true }); setValue('password', '', { shouldDirty: true, shouldValidate: true }); }, [reset, setValue, trigger]);

    useEffect(() => {
        if (Array.isArray(login?.errors)) {
            login.errors.forEach((error) => setError(error.type, { type: 'manual', message: error.message }));
            return;
        }

        const candidate = (login?.errors && typeof login.errors === 'object') ? login.errors : ((login?.error && typeof login.error === 'object') ? login.error : null);
        if (!candidate) return;

        const knownFields = Object.keys(defaultValues);

        if (candidate?.properties) {
            const applied = _applyPropertiesErrors(candidate.properties, setError, knownFields, 'email');
            if (applied) return;
        }

        const msg = _backendMsg(candidate);
        if (msg) setError('email', { type: 'server', message: msg });
    }, [login?.errors, login?.error, setError]);

    const messageConfig = useMemo(() => {
        if (login?.nextStep === 'noGroup') return NO_GROUP;
        if (login?.nextStep === 'noRole') return NO_ROLE;
        return null;
    }, [login?.nextStep]);

    useEffect(() => {
        if (isChoosing) { if (!choiceFlow) setChoiceFlow(true); return; }
        if (!inProgress && !login?.isLoggedIn && !login?.success && !messageConfig) { if (choiceFlow) setChoiceFlow(false); }
    }, [isChoosing, inProgress, login?.isLoggedIn, login?.success, messageConfig, choiceFlow]);

    function onSubmit(model) { dispatch(submitLogin(model)); }

    const handleSelectGroup = (group) => { setChoiceFlow(true); dispatch(submitGroupChoice(group.id)); };
    const handleRefreshGroups = () => console.log('[chooseGroup] refresh');

    const handleSelectRole = (role) => { setChoiceFlow(true); dispatch(submitRoleChoice(role)); };
    const handleRefreshRoles = () => console.log('[chooseRole] refresh');

    const groups = login?.meta?.groups ?? login?.client?.userGroups ?? [];
    const roles = login?.meta?.roles ?? login?.client?.roles ?? [];
    const handleSamlLogin = () => window.location.assign(SAML_REDIRECT_URL);

    const hardDisabled = !!login?.success || !!login?.isLoggedIn;
    const backVisible = isChoosing || choiceFlow;
    const backDisabled = inProgress || hardDisabled;

    const authButtonsDisabled = inProgress || hardDisabled || choiceFlow;

    const mailtoHref = useMemo(() => {
        if (!messageConfig) return '';
        const u = login?.client || {};
        const body = String(messageConfig.emailcontent || '')
            .replaceAll('{email}', String(u.email || ''))
            .replaceAll('{name}', String(u.name || ''))
            .replaceAll('{purpose}', '')
            .replaceAll('{phone}', '');
        return _mailto({ to: messageConfig.emailrecipient, subject: messageConfig.emailsubject, body });
    }, [messageConfig, login?.client]);

    const backToLogin = async () => {
        setChoiceFlow(false);
        setEmailLoginMode(!SAML_LOGIN);
        try { await dispatch(logoutUserSilent()); } catch (e) { }
        dispatch(resetLoginState());
        reset(defaultValues);
        setValue('email', '', { shouldDirty: true, shouldValidate: true });
        setValue('password', '', { shouldDirty: true, shouldValidate: true });
    };

    const backToSelector = () => {
        setChoiceFlow(false);
        setEmailLoginMode(!SAML_LOGIN);
        dispatch(resetLoginState());
        reset(defaultValues);
        setValue('email', '', { shouldDirty: true, shouldValidate: true });
        setValue('password', '', { shouldDirty: true, shouldValidate: true });
    };

    const onMailto = () => { if (mailtoHref) window.open(mailtoHref, '_blank', 'noopener,noreferrer'); };

    const showSelector = SAML_LOGIN && !messageConfig && !isChoosing && !emailLoginMode;
    const showEmailForm = !messageConfig && !isChoosing && emailLoginMode;
    const showChoicePanels = !messageConfig && isChoosing;

    const selectorButtonsDisabled = inProgress || hardDisabled || choiceFlow;

    return (
        <div className={clsx(classes.root, 'flex flex-col flex-auto items-center justify-center flex-shrink-0 p-16 md:p-24')}>
            <motion.div initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} className="flex min-w-320 max-w-400 md:max-w-3xl rounded-20 shadow-2xl overflow-hidden">
                <Card className={clsx(classes.leftSection, 'flex flex-col w-full max-w-sm items-center justify-center shadow-0 bg-transparent py-0')} square>
                    <CardContent className="flex flex-col gap-0 items-center justify-center w-full px-0 py-0 pb-0 max-w-320 bg-white bg-opacity-95" style={{ paddingBottom: '0px' }}>

                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, transition: { delay: 0.2 } }}>
                            <div className="flex items-center py-24">
                                <img className="w-full h-auto" src="assets/images/logos/nboss_logo_big.png" alt="nBoss Admin" />
                            </div>
                        </motion.div>

                        <div className="w-full bg-white rounded-2xl px-20 py-24 shadow-2xl">
                            {messageConfig ? (
                                <div className="w-full flex flex-col gap-12 text-center">
                                    <Typography className="text-18 font-semibold">{messageConfig.title}</Typography>
                                    <Typography className="text-13 opacity-80">{messageConfig.content}</Typography>
                                    <Divider className="my-8" />
                                    <div className="w-full flex flex-col gap-8">
                                        <Button type="button" variant="contained" color="primary" className="w-full" disabled={inProgress} onClick={backToLogin}>Vissza a bejelentkezéshez</Button>
                                        <Button type="button" variant="contained" color="secondary" className="w-full" disabled={inProgress} onClick={onMailto}>Kapcsolatfelvétel adminnal</Button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {showSelector && (
                                        <div className="w-full flex flex-col">
                                            <div className="w-full flex flex-col gap-4 text-center mb-20">
                                                <Typography className="text-12 opacity-70">Válassz egy bejelentkezési módot a folytatáshoz.</Typography>
                                            </div>

                                            <Button type="button" variant="contained" color="secondary" className="w-full" aria-label="SAML LOGIN" disabled={selectorButtonsDisabled} onClick={handleSamlLogin}>
                                                MOL fiókos belépés
                                            </Button>

                                            <Typography className="text-12 opacity-60 text-center my-8">
                                                vagy
                                            </Typography>

                                            <Button type="button" variant="contained" color="primary" className="w-full" aria-label="EMAIL LOGIN" disabled={selectorButtonsDisabled} onClick={() => setEmailLoginMode(true)}>
                                                Emailes bejelentkezés
                                            </Button>
                                        </div>
                                    )}

                                    {showEmailForm && (
                                        <form className="flex flex-col justify-center w-full" onSubmit={handleSubmit(onSubmit)}>
                                            <Controller name="email" control={control} render={({ field }) => (
                                                <TextField {...field} autoComplete="username" className="mb-16" type="text" error={!!errors.email} helperText={errors?.email?.message} label={t('EMAIL')} InputProps={{ className: 'pr-4', endAdornment: (<InputAdornment position="end" className="ml-4"><Icon className="text-20 mx-12" color="action">email</Icon></InputAdornment>) }} variant="outlined" required />
                                            )} />

                                            <Controller name="password" control={control} render={({ field }) => (
                                                <TextField {...field} autoComplete="current-password" className="mb-16" label={t('PASSWORD')} type="password" error={!!errors.password} helperText={errors?.password?.message} variant="outlined" InputProps={{ className: 'pr-4', type: showPassword ? 'text' : 'password', endAdornment: (<InputAdornment position="end" className="ml-4"><IconButton onClick={() => setShowPassword(!showPassword)}><Icon className="text-20" color="action">{showPassword ? 'visibility' : 'visibility_off'}</Icon></IconButton></InputAdornment>) }} required />
                                            )} />

                                            <div className="w-full flex flex-col gap-8 mt-8">
                                                <Button type="submit" variant="contained" color="primary" className="w-full" aria-label="LOG IN" disabled={authButtonsDisabled || _.isEmpty(dirtyFields) || !isValid} value="legacy">
                                                    {t('LOGIN')}
                                                </Button>

                                                {SAML_LOGIN && (
                                                    <Button type="button" variant="contained" color="primary" className="w-full" disabled={inProgress || hardDisabled} onClick={backToSelector}>
                                                        Vissza
                                                    </Button>
                                                )}
                                            </div>
                                        </form>
                                    )}

                                    {showChoicePanels && (
                                        <div className="w-full flex flex-col gap-12">
                                            {login?.nextStep === 'chooseRole' && <ChooseRolePanel roles={roles} onSelect={handleSelectRole} onRefresh={handleRefreshRoles} disabled={inProgress} />}
                                            {login?.nextStep === 'chooseGroup' && <ChooseGroupPanel groups={groups} onSelect={handleSelectGroup} onRefresh={handleRefreshGroups} disabled={inProgress} />}

                                            {(backVisible && login?.nextStep) && (
                                                <Button type="button" variant="contained" color="primary" className="w-full" disabled={backDisabled} onClick={backToLogin}>
                                                    Mégse
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {!messageConfig && showEmailForm && !isChoosing && (
                            <div className="flex flex-col items-center justify-center py-20">
                                <div><span className="font-normal opacity-70">{t('LOST_YOUR_PASSWORD')} </span><Link style={{ color: '#252F2E' }} className='font-semibold' to="/password-reset">{t('REQUEST_A_NEW_PASSWORD')}</Link></div>
                            </div>
                        )}

                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}

export default Login;